/** Popups de evento (Apontamento/Compromisso/Ausência) + dialog "mais +N" do dia. */
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Bell,
  CalendarDays,
  CheckCheck,
  CheckCircle2,
  ClipboardCheck,
  ExternalLink,
  ListChecks,
  Pencil,
  RotateCcw,
  Trash2,
  UserRound,
} from 'lucide-react';
import type { Absence, AgendaAppointmentEvent, Commitment } from '@erp/shared';
import { usePermissions } from '@/auth/use-permissions';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/misc';
import { useQuery } from '@tanstack/react-query';
import {
  CloseReasonFields,
  EMPTY_CLOSE_REASON,
  closeBodyOf,
  closeReasonMissing,
  type CloseReasonValue,
} from '@/features/tickets/close-ticket-dialog';
import { ticketCloseOptionsQuery } from '@/features/tickets/tickets-queries';
import { errorMessage } from './agenda-queries';
import {
  useConcludeCommitment,
  useDeleteAbsence,
  useDeleteCommitment,
  useToggleCommitmentDone,
} from './agenda-mutations';
import {
  canConcludeTicket,
  concludeConfirmText,
  concludeNotice,
  doneAppointmentLine,
  doneErrorMessage,
  doneNotice,
} from './commitment-done';
import {
  capitalize,
  commitmentWhen,
  formatBRDate,
  parseDay,
  type AgendaEventItem,
} from './agenda-format';
import { CommitmentSituationBadge, situationOf } from './commitment-situation';
import { HiddenTicketBadge } from './hidden-ticket-mark';
import { FullEventRow, eventKey, type AgendaRowMode } from './event-rows';

function initialOf(name: string): string {
  return (name.trim().charAt(0) || '?').toUpperCase();
}

/** Descrição com line-clamp + 'Ver mais/menos' quando longa. */
function ExpandableText({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = text.length > 160 || text.split('\n').length > 3;
  return (
    <div className="text-sm text-foreground">
      <p className={expanded ? 'whitespace-pre-wrap' : 'line-clamp-3 whitespace-pre-wrap'}>
        {text}
      </p>
      {isLong ? (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 cursor-pointer text-xs font-medium text-primary hover:underline"
        >
          {expanded ? 'Ver menos' : 'Ver mais'}
        </button>
      ) : null}
    </div>
  );
}

function TicketLink({
  ticketNumber,
  title,
  onClose,
}: {
  ticketNumber: number;
  title: string;
  onClose: () => void;
}) {
  return (
    <Link
      to="/tickets/$ticketNumber"
      params={{ ticketNumber: String(ticketNumber) }}
      onClick={onClose}
      className="block truncate text-sm font-semibold text-primary hover:underline"
    >
      #{ticketNumber} - {title.toUpperCase()}
    </Link>
  );
}

// ── Apontamento ─────────────────────────────────────────────────────────────

export function AppointmentDialog({
  appointment,
  onClose,
}: {
  appointment: AgendaAppointmentEvent;
  onClose: () => void;
}) {
  const a = appointment;
  return (
    <Dialog open onClose={onClose} title="Apontamento">
      <div className="rounded-lg border p-4">
        <div className="flex items-start gap-3">
          <Avatar>
            <AvatarFallback>{initialOf(a.userName)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1 space-y-1.5">
            <TicketLink ticketNumber={a.ticketNumber} title={a.ticketTitle} onClose={onClose} />
            <div className="text-sm text-muted-foreground">
              {formatBRDate(a.date)} - {formatBRDate(a.date)}
            </div>
            <div className="flex items-center gap-1.5 text-sm">
              <UserRound className="size-4 shrink-0 text-muted-foreground" aria-hidden />
              <span className="truncate">{a.clientName}</span>
            </div>
            <div className="text-sm tabular-nums">
              {a.startTime}
              {a.endTime ? ` - ${a.endTime}` : ' - em andamento'}
            </div>
            {a.description ? <ExpandableText text={a.description} /> : null}
          </div>
        </div>
      </div>
    </Dialog>
  );
}

// ── Compromisso ─────────────────────────────────────────────────────────────

/**
 * O compromisso aberto (as duas abas). spec 039: a placa, o ticket e o
 * cliente; a mesa e a etapa ATUAIS do ticket, para acompanhar o andamento; a
 * situação pela regra única do @erp/shared; e as ações — abrir o ticket,
 * marcar como feito (ou desfazer), editar para reagendar e excluir.
 *
 * Adendo: ticket oculto pela ocultação do tenant leva a marca "Oculto no ERP"
 * e não se abre — nem pelo nº, nem por "Abrir ticket": o detalhe dele é 404.
 *
 * Adendo 4: "Marcar como feito" gera o apontamento no ticket, e a tela avisa
 * pelo que a API devolveu ("Feito. Apontamento registrado no ticket #N"; no
 * espelhado, que o apontamento é feito no TiFlux). "Concluir e encerrar o
 * ticket" faz o feito e o encerramento numa operação só, com confirmação.
 */
export function CommitmentDialog({
  commitment,
  onEdit,
  onClose,
}: {
  commitment: Commitment;
  onEdit: () => void;
  onClose: () => void;
}) {
  const c = commitment;
  const toggle = useToggleCommitmentDone();
  const conclude = useConcludeCommitment();
  const remove = useDeleteCommitment();
  const { can, ready: permsReady } = usePermissions();
  // o aviso do que aconteceu mora DENTRO do diálogo, como status: o modal
  // esconde de leitor de tela tudo o que está fora dele (um toast lá fora
  // seria só visual)
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmingConclude, setConfirmingConclude] = useState(false);
  const situation = situationOf(c);
  const deskStage = [c.ticketDeskName, c.ticketStageName].filter(Boolean).join(' · ');
  const appointmentLine = doneAppointmentLine(c);

  const toggleDone = () => {
    const before = c;
    setNotice(null);
    toggle.mutate(
      { id: c.id, done: !c.done },
      { onSuccess: (saved) => saved && setNotice(doneNotice(before, saved)) }
    );
  };

  if (confirmingConclude) {
    return (
      <ConcludeTicketDialog
        commitment={c}
        conclude={conclude}
        onBack={() => setConfirmingConclude(false)}
        onClose={onClose}
        onConcluded={(saved) => {
          setConfirmingConclude(false);
          setNotice(concludeNotice(saved));
        }}
      />
    );
  }

  if (confirmingDelete) {
    return (
      <Dialog open onClose={onClose} title="Excluir compromisso">
        <p className="text-sm text-muted-foreground">
          Tem certeza que deseja excluir o compromisso do ticket{' '}
          <strong className="text-foreground">#{c.ticketNumber}</strong>? Essa ação não pode ser
          desfeita.
        </p>
        {remove.error ? (
          <p className="mt-3 text-sm text-destructive">{errorMessage(remove.error)}</p>
        ) : null}
        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setConfirmingDelete(false)}
          >
            Voltar
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            disabled={remove.isPending}
            onClick={() => remove.mutate(c.id, { onSuccess: onClose })}
          >
            {remove.isPending ? 'Excluindo…' : 'Excluir'}
          </Button>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={onClose} title="Compromisso">
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <CommitmentSituationBadge situation={situation} />
          {c.ticketHidden ? <HiddenTicketBadge /> : null}
        </div>
        {c.ticketPlate ? (
          <div className="text-lg leading-6 font-semibold tracking-wide">{c.ticketPlate}</div>
        ) : null}
        {c.ticketHidden ? (
          <div className="truncate text-sm font-semibold">
            #{c.ticketNumber} - {c.ticketTitle.toUpperCase()}
          </div>
        ) : (
          <TicketLink ticketNumber={c.ticketNumber} title={c.ticketTitle} onClose={onClose} />
        )}
        <div className="flex items-center gap-1.5 text-sm">
          <UserRound className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          <span className="truncate">{c.clientName}</span>
        </div>
        {deskStage ? (
          <div className="flex items-center gap-1.5 text-sm">
            <ListChecks className="size-4 shrink-0 text-muted-foreground" aria-hidden />
            <span className="truncate" title="Mesa e etapa atuais do ticket">
              {deskStage}
            </span>
          </div>
        ) : null}
        <div className="flex items-start gap-1.5 text-sm">
          <CalendarDays className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
          <div className="min-w-0">
            <div>
              {formatBRDate(c.startDate)} - {formatBRDate(c.endDate)}
            </div>
            <div className="text-muted-foreground">{commitmentWhen(c)}</div>
          </div>
        </div>
        {c.assigneeUserId !== null && c.assigneeName ? (
          <div className="flex items-center gap-2 text-sm">
            <Avatar size="sm">
              <AvatarFallback>{initialOf(c.assigneeName)}</AvatarFallback>
            </Avatar>
            <span className="truncate">{c.assigneeName}</span>
          </div>
        ) : c.calendarName ? (
          <div className="flex items-center gap-2 text-sm">
            <span
              className="size-2.5 shrink-0 rounded-full"
              style={{ background: c.calendarColor ?? undefined }}
              aria-hidden
            />
            <span className="truncate">{c.calendarName}</span>
          </div>
        ) : null}
        <div className="flex items-center gap-1.5 text-sm">
          <Bell className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          {c.reminderMinutes} minutos antes
        </div>
        {c.description ? <ExpandableText text={c.description} /> : null}
        {appointmentLine ? (
          <div className="flex items-center gap-1.5 text-sm">
            <ClipboardCheck className="size-4 shrink-0 text-muted-foreground" aria-hidden />
            <span>{appointmentLine}</span>
          </div>
        ) : null}
      </div>

      {notice && !toggle.isPending ? (
        <p role="status" className="mt-3 flex items-center gap-1.5 text-sm font-medium text-primary">
          <CheckCircle2 className="size-4 shrink-0" aria-hidden />
          {notice}
        </p>
      ) : null}
      {toggle.error ? (
        <p className="mt-3 text-sm text-destructive">
          {doneErrorMessage(toggle.error) ?? errorMessage(toggle.error)}
        </p>
      ) : null}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-divider pt-3">
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant={c.done ? 'outline' : 'default'}
            size="sm"
            disabled={toggle.isPending || conclude.isPending}
            onClick={toggleDone}
          >
            {c.done ? <RotateCcw /> : <CheckCircle2 />}
            {c.done ? 'Desfazer feito' : 'Marcar como feito'}
          </Button>
          {permsReady && canConcludeTicket(c, can) ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={toggle.isPending}
              onClick={() => {
                conclude.reset();
                setConfirmingConclude(true);
              }}
            >
              <CheckCheck />
              Concluir e encerrar o ticket
            </Button>
          ) : null}
          {c.ticketHidden ? null : (
            <Button type="button" variant="outline" size="sm" asChild>
              <Link
                to="/tickets/$ticketNumber"
                params={{ ticketNumber: String(c.ticketNumber) }}
                onClick={onClose}
              >
                <ExternalLink />
                Abrir ticket
              </Link>
            </Button>
          )}
        </div>
        <div className="flex gap-1">
          <Button type="button" variant="ghost" size="icon-sm" onClick={onEdit} aria-label="Editar compromisso">
            <Pencil />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={() => setConfirmingDelete(true)}
            aria-label="Excluir compromisso"
          >
            <Trash2 />
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ── Ausência ────────────────────────────────────────────────────────────────

export function AbsenceViewDialog({
  absence,
  onEdit,
  onClose,
}: {
  absence: Absence;
  onEdit: () => void;
  onClose: () => void;
}) {
  const ab = absence;
  const remove = useDeleteAbsence();
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  if (confirmingDelete) {
    return (
      <Dialog open onClose={onClose} title="Excluir ausência">
        <p className="text-sm text-muted-foreground">
          Tem certeza que deseja excluir a ausência de{' '}
          <strong className="text-foreground">{ab.userName}</strong>?
        </p>
        {remove.error ? (
          <p className="mt-3 text-sm text-destructive">{errorMessage(remove.error)}</p>
        ) : null}
        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setConfirmingDelete(false)}
          >
            Voltar
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            disabled={remove.isPending}
            onClick={() => remove.mutate(ab.id, { onSuccess: onClose })}
          >
            {remove.isPending ? 'Excluindo…' : 'Excluir'}
          </Button>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={onClose} title="Ausência">
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm">
          <Avatar size="sm">
            <AvatarFallback>{initialOf(ab.userName)}</AvatarFallback>
          </Avatar>
          <span className="truncate font-medium">{ab.userName}</span>
        </div>
        <div className="flex items-center gap-1.5 text-sm">
          <CalendarDays className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          {formatBRDate(ab.startDate)} - {formatBRDate(ab.endDate)}
        </div>
        {ab.description ? <ExpandableText text={ab.description} /> : null}
      </div>
      <div className="mt-4 flex justify-end gap-1 border-t border-divider pt-3">
        <Button type="button" variant="ghost" size="icon-sm" onClick={onEdit} aria-label="Editar ausência">
          <Pencil />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={() => setConfirmingDelete(true)}
          aria-label="Excluir ausência"
        >
          <Trash2 />
        </Button>
      </div>
    </Dialog>
  );
}

// ── "mais +N": lista completa do dia ────────────────────────────────────────

export function DayEventsDialog({
  dayISO,
  events,
  onSelect,
  onClose,
  mode = 'general',
}: {
  dayISO: string;
  events: AgendaEventItem[];
  onSelect: (item: AgendaEventItem) => void;
  onClose: () => void;
  mode?: AgendaRowMode;
}) {
  const title = capitalize(format(parseDay(dayISO), "EEEE, d 'de' MMMM", { locale: ptBR }));
  return (
    <Dialog open onClose={onClose} title={title}>
      <div className="space-y-1">
        {events.map((item) => (
          <FullEventRow key={eventKey(item)} item={item} onClick={onSelect} mode={mode} />
        ))}
      </div>
    </Dialog>
  );
}

/**
 * A confirmação do "Concluir e encerrar o ticket" (spec 039, adendo 4) com o
 * motivo de fechamento da mesa do ticket (spec 054): na mesa com motivo, só
 * conclui depois de escolher, e sem ele a API recusa e nada fica feito.
 */
function ConcludeTicketDialog({
  commitment: c,
  conclude,
  onBack,
  onClose,
  onConcluded,
}: {
  commitment: Commitment;
  conclude: ReturnType<typeof useConcludeCommitment>;
  onBack: () => void;
  onClose: () => void;
  onConcluded: (saved: Commitment) => void;
}) {
  const optionsQ = useQuery(ticketCloseOptionsQuery(c.ticketNumber));
  const [value, setValue] = useState<CloseReasonValue>(EMPTY_CLOSE_REASON);
  const missing = closeReasonMissing(optionsQ.data, value, optionsQ.isError);
  return (
    <Dialog open onClose={onClose} title="Concluir e encerrar o ticket">
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (missing) return;
          conclude.mutate({ id: c.id, close: closeBodyOf(value) }, { onSuccess: onConcluded });
        }}
      >
        <p className="text-sm text-muted-foreground">{concludeConfirmText(c)}</p>
        {optionsQ.isError ? (
          <p className="text-sm text-muted-foreground">
            Não deu para carregar os motivos desta mesa. Se ela exigir um, a conclusão vai avisar.
          </p>
        ) : (
          <CloseReasonFields
            idPrefix="conclude"
            options={optionsQ.data}
            loading={optionsQ.isPending}
            value={value}
            onChange={setValue}
            disabled={conclude.isPending}
          />
        )}
        {conclude.error ? (
          <p className="text-sm text-destructive">
            {doneErrorMessage(conclude.error) ?? errorMessage(conclude.error)}
          </p>
        ) : null}
        <div className="mt-1 flex justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onBack}>
            Voltar
          </Button>
          <Button type="submit" size="sm" disabled={conclude.isPending || missing}>
            {conclude.isPending ? 'Concluindo…' : 'Concluir e encerrar'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
